import React, { useContext } from "react";
import { Box } from "@material-ui/core";
import BallotIcon from "@material-ui/icons/Ballot";
import Gallery from "../../../components/Gallery/Gallery";
import AttachmentApi from "../../../api/services/AttachmentApi";
import AttachmentFileHandler from "../../../helper/AttachmentFileHandler";
import TestRequestApi from "../../../api/services/TestRequestApi";
import FileReference from "../../../components/Gallery/model/FileReference.model";
import LoadedFile from "../../../components/Gallery/model/LoadedFile.model";
import NamingStrategy from "../../../components/Gallery/model/NamingStrategy";
import useEnv from "../../../hooks/env";
import { GalleryContextProvider } from "../../../contexts/GalleryContext";
import StyledTooltip from "../../../components/Wrapper/StyledTooltip";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import GalleryHeader from "../../../components/Gallery/GalleryHeader";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

type TestRequestDescriptionAttachment = Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>;

export interface TestRequestGalleryProps {
  disabled: boolean;
  fileReferences: TestRequestDescriptionAttachment[];
  isInCreateMode: boolean;
  onSave: () => void;
  testRequestId: string;
}

const infoText = `Use this upload section for any file related to the general request
      (e.g. information from the customer, pictures from the application, communication 
      with the customer, etc...)`;

const TestRequestGallery: React.FC<TestRequestGalleryProps> = (props) => {
  const { disabled, fileReferences, isInCreateMode, onSave, testRequestId } = props;

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleDialogNegativeCallback } = useContext(ErrorSnackbarContext);

  if (isInCreateMode) {
    return (
      <>
        <GalleryHeader infoText={infoText} />
        <Box display="inline-block">
          <GalleryButton
            onClick={() => {
              showConfirmationDialog({
                headline: "SAVE TEST REQUEST",
                text: "You have to save the test request to add attachments.",
                confirmText: "Save",
              })
                .then(onSave)
                .catch(handleDialogNegativeCallback);
            }}
            text="Add Picture or Document"
            icon="ADD"
          />
        </Box>
      </>
    );
  }
  const { api } = useEnv();
  const attachmentApi = new AttachmentApi(api);
  const testRequestApi = new TestRequestApi(api);
  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) =>
        testRequestApi
          .removeAttachmentFromRequest(testRequestId, idToBeDeleted)
          .then((attachments) => ({ attachments, idToBeDeleted })),
      append: (ids) => testRequestApi.addAttachmentToRequest(testRequestId, ids),
      zipFileName(files: FileReference[]): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return (file as LoadedFile).name;
          }
          return file.id;
        }
        return `LEAP${testRequestId}-Description.zip`;
      },
    },
    () => testRequestApi.getTestRequestAttachmentClearanceToken(testRequestId).then((res) => res.data),
  );

  const namingStrategy: NamingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY ${testRequestId} Description`,
        subheader: file.name,
      };
    },
  };

  return (
    <GalleryContextProvider>
      <Gallery
        filterable
        disabled={disabled}
        galleryId={testRequestId}
        fileHandler={attachmentFileHandler}
        namingStrategy={namingStrategy}
        attachments={fileReferences}
        testRequestId={testRequestId}
        shouldRenderIndicator={(loadedFile) =>
          loadedFile.tags.includes("specification")
            ? {
                title: "Specification Document",
                icon: <BallotIcon />,
              }
            : null
        }
        infoText={infoText}
      />
    </GalleryContextProvider>
  );
};

export default TestRequestGallery;
